import { Instant } from '@js-joda/core';
import type { FlattenedLocateResultRow } from '@thinkalpha/ui-types/types/labModern';
import type { DataRow, FetchMoreResult } from 'src/features/InfiniteTimeView';

export type LocatesModalAnswer = boolean;

export interface LocatesRow extends DataRow {
    quantity: number;
    rate: number;
    symbol: string;
}

export interface LocateResultRow extends DataRow {
    orderId: string;
    resultId: string;
    stableOrderId: string;
    symbol: string;
    accountId: string;
    createdAt: Instant;
    createdBy: string;
    marketParticipantId: string;
    orderQuantity: number;
    orderState: string;
    routeName: string | null;
    offeredQuantity: number | null;
    price: number | null;
    billingPrice: number | null;
    providerId: string;
    providerName?: string;
    resultState: string;
    acceptedAt: Instant | null;
    actionableAt: Instant | null;
    rejectedAt: Instant | null;
    rejectedBy: string | null;
    rejectReason: string | null;
    injectionTargetName?: string;
    integration: string | null;
}

export async function fetchLocatesData(
    _anchor: LocatesRow | null,
    _direction: 'up' | 'down' | 'initial',
    expectedCount: number = 20,
): Promise<FetchMoreResult<LocatesRow>> {
    const rows: LocatesRow[] = [];
    for (let i = 0; i < expectedCount; i++) {
        rows.push({
            type: 'data',
            id: `locate-${i}`,
            symbol: `SYM${i}`,
            quantity: Math.floor(Math.random() * 10000),
            rate: parseFloat((Math.random() * 10).toFixed(2)),
        });
    }

    return {
        rows,
        isEndOfDataUpward: true,
        isEndOfDataDownward: true,
    };
}

export async function fetchLocateResultsData(
    _anchor: LocateResultRow | null,
    _direction: 'up' | 'down' | 'initial',
    expectedCount: number = 20,
): Promise<FetchMoreResult<LocateResultRow>> {
    const rows: LocateResultRow[] = [];

    const symbols = ['AAPL', 'GOOGL', 'MSFT', 'TSLA', 'NVDA', 'AMD', 'META', 'AMZN'];
    const states = ['Quoted', 'Offered', 'Accepted', 'Rejected'];
    const providers = ['Provider-A', 'Provider-B', 'Provider-C', 'Provider-D'];
    const routes = ['Route-Alpha', 'Route-Beta', 'Route-Gamma', null];
    const users = ['trader1', 'trader2', 'trader3'];
    const accounts = ['ACC-001', 'ACC-002', 'ACC-003'];
    const marketParticipants = ['MP-123', 'MP-456', 'MP-789'];

    const numOrders = Math.max(3, Math.ceil(expectedCount / 3));
    let rowId = 0;

    for (let orderIdx = 0; orderIdx < numOrders; orderIdx++) {
        const orderId = `order-${orderIdx}`;
        const stableOrderId = `stable-${orderId}`;
        const symbol = symbols[orderIdx % symbols.length];
        const orderQuantity = Math.floor(Math.random() * 5000) + 1000;
        const createdAt = Instant.now().minusSeconds(Math.floor(Math.random() * 3600));
        const createdBy = users[Math.floor(Math.random() * users.length)];
        const accountId = accounts[Math.floor(Math.random() * accounts.length)];
        const marketParticipantId = marketParticipants[Math.floor(Math.random() * marketParticipants.length)];
        const routeName = routes[Math.floor(Math.random() * routes.length)];
        const orderState = orderIdx % 4 === 0 ? 'complete' : orderIdx % 3 === 0 ? 'pending' : 'actionable';

        const numResults = Math.floor(Math.random() * 3) + 1;

        for (let resultIdx = 0; resultIdx < numResults; resultIdx++) {
            const resultId = `result-${orderIdx}-${resultIdx}`;
            const providerId = `provider-${resultIdx}`;
            const providerName = providers[resultIdx % providers.length];
            const resultState = states[Math.floor(Math.random() * states.length)];
            const integration = `integration-${resultIdx % 2}`;

            const offeredQuantity =
                resultState !== 'Quoted' ? Math.floor(orderQuantity * (0.5 + Math.random() * 0.5)) : null;
            const price = resultState !== 'Quoted' ? parseFloat((Math.random() * 0.5 + 0.1).toFixed(4)) : null;
            const billingPrice = price ? parseFloat((price * 1.1).toFixed(4)) : null;

            const actionableAt =
                resultState === 'Offered' || resultState === 'Accepted'
                    ? createdAt.plusSeconds(Math.floor(Math.random() * 60) + 10)
                    : null;
            const acceptedAt = resultState === 'Accepted' ? (actionableAt?.plusSeconds(5) ?? null) : null;
            const rejectedAt = resultState === 'Rejected' ? (actionableAt?.plusSeconds(5) ?? null) : null;
            const rejectedBy = resultState === 'Rejected' ? createdBy : null;
            const rejectReason = resultState === 'Rejected' ? 'Price too high' : null;

            rows.push({
                type: 'data',
                id: `${orderId}-${resultId}`,
                orderId,
                resultId,
                stableOrderId,
                symbol,
                accountId,
                createdAt,
                createdBy,
                marketParticipantId,
                orderQuantity,
                orderState,
                routeName,
                offeredQuantity,
                price,
                billingPrice,
                providerId,
                providerName,
                resultState,
                acceptedAt,
                actionableAt,
                rejectedAt,
                rejectedBy,
                rejectReason,
                integration,
            });

            rowId++;
            if (rowId >= expectedCount) {
                return {
                    rows,
                    isEndOfDataUpward: true,
                    isEndOfDataDownward: true,
                };
            }
        }
    }

    return {
        rows,
        isEndOfDataUpward: true,
        isEndOfDataDownward: true,
    };
}

export function locatesRowComparator(a: LocatesRow, b: LocatesRow): number {
    return a.id.localeCompare(b.id);
}

export function locateResultRowComparator(a: LocateResultRow, b: LocateResultRow): number {
    const timeCompare = b.createdAt.compareTo(a.createdAt);
    if (timeCompare !== 0) {
        return timeCompare;
    }
    return a.id.localeCompare(b.id);
}

export function noopDecoratorFunction(_row1: LocatesRow | undefined, _row2: LocatesRow | undefined): undefined {
    return undefined;
}

export function noopLocateResultDecoratorFunction(
    _row1: LocateResultRow | undefined,
    _row2: LocateResultRow | undefined,
): undefined {
    return undefined;
}

export function noopFlattenedLocateResultDecoratorFunction(
    _row1: FlattenedLocateResultRow | undefined,
    _row2: FlattenedLocateResultRow | undefined,
): undefined {
    return undefined;
}
